// src/hooks/useSubscription.js
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";

/**
 * useSubscription
 * Fetches /auth/subscription-status once, exposes normalized flags + a manual refresh().
 *
 * Returns:
 *  - status: "ACTIVE" | "TRIALING" | "INACTIVE" | "UNKNOWN"
 *  - meta: {
 *      subscription_cancel_at_period_end,
 *      subscription_cancel_at_iso,
 *      subscription_current_period_end_iso,
 *      trial_ends_at_iso,
 *      trial_days_left
 *    }
 *  - flags: { isActive, isTrialing, inTrial, isCanceling }
 *  - loading, error, refresh()
 */
export default function useSubscription({ enabled = true } = {}) {
  const [status, setStatus] = useState("UNKNOWN");
  const [meta, setMeta] = useState({
    stripe_subscription_id: null,
    subscription_cancel_at_period_end: false,
    subscription_cancel_at_iso: null,
    subscription_current_period_end_iso: null,
    trial_ends_at_iso: null,
    trial_days_left: 0,
  });
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  const normalizeStatus = (s) => (s ? String(s).toUpperCase() : "INACTIVE");

  // Guards to prevent overlapping calls and state updates after unmount
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || inFlightRef.current) return;

    inFlightRef.current = true;
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      const { data } = await axios.get("https://ghrianghraffy.com/auth/subscription-status", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!mountedRef.current) return;

      setStatus(normalizeStatus(data.status));
      setMeta({
        stripe_subscription_id: data.stripe_subscription_id || null,
        subscription_cancel_at_period_end: !!data.subscription_cancel_at_period_end,
        subscription_cancel_at_iso: data.subscription_cancel_at_iso || null,
        subscription_current_period_end_iso: data.subscription_current_period_end_iso || null,
        trial_ends_at_iso: data.trial_ends_at_iso || null,
        trial_days_left: Number.isFinite(data.trial_days_left) ? Number(data.trial_days_left) : 0,
      });
    } catch (e) {
      if (!mountedRef.current) return;

      setStatus("INACTIVE");
      setMeta({
        stripe_subscription_id: null,
        subscription_cancel_at_period_end: false,
        subscription_cancel_at_iso: null,
        subscription_current_period_end_iso: null,
        trial_ends_at_iso: null,
        trial_days_left: 0,
      });
      setError(e);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled]);

  // initial + when enabled flips on
  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  // convenience flags
  const flags = useMemo(() => {
    const isActive = status === "ACTIVE";
    const isTrialing = status === "TRIALING";
    const inTrial = isTrialing && meta.trial_days_left > 0; // server-managed trial window
    const isCanceling = isActive && !!meta.subscription_cancel_at_period_end;
    return { isActive, isTrialing, inTrial, isCanceling };
  }, [status, meta]);

  return { status, meta, flags, loading, error, refresh };
}

/** Small helper for UI */
export const formatLocalDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

